import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { X, LayoutTemplate } from "lucide-react";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function displayEnd(end) {
  return end === "00:00" ? "24:00" : end;
}

/**
 * Bulk apply a shift template to selected employees × selected days.
 * Adds NEW shifts to the schedule via onApply(newShifts).
 */
export default function ApplyTemplateModal({ employees, weekStart, onClose, onApply, department }) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [employeeIds, setEmployeeIds] = useState([]);
  const [days, setDays] = useState([0, 1, 2, 3, 4]); // Mon-Fri default
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/shift_templates").then(({ data }) => {
      const active = data.filter((t) => t.active !== false);
      setTemplates(active);
      if (active.length && !templateId) setTemplateId(active[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, []); // eslint-disable-line

  const template = useMemo(() => templates.find((t) => t.id === templateId), [templates, templateId]);

  const toggleEmp = (id) =>
    setEmployeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleDay = (i) =>
    setDays((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  const totalShifts = employeeIds.length * days.length;

  const apply = () => {
    if (!template) return toast.error("Pick a template");
    if (employeeIds.length === 0) return toast.error("Select at least one employee");
    if (days.length === 0) return toast.error("Select at least one day");
    setBusy(true);
    try {
      const newShifts = [];
      for (const empId of employeeIds) {
        const emp = employees.find((e) => e.id === empId);
        for (const d of days) {
          newShifts.push({
            employee_id: empId,
            employee_name: emp?.name || "",
            date: addDays(weekStart, d),
            start_time: template.start_time,
            end_time: template.end_time,
            break_minutes: Number(template.break_minutes) || 0,
            role: template.role || (department === "All" ? "Floor" : department),
            department: template.department || (department === "All" ? "Floor" : department),
            shift_type: template.shift_type || "mid",
            notes: template.notes || "",
          });
        }
      }
      onApply(newShifts);
      toast.success(`Added ${newShifts.length} shift${newShifts.length === 1 ? "" : "s"}`);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="apply-template-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-2xl flex items-center gap-2"><LayoutTemplate size={20}/> Apply Shift Template</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>

        {templates.length === 0 ? (
          <div className="p-4 border border-[color:var(--warning)] bg-[color:var(--warning)]/10 rounded-lg text-sm">
            No shift templates yet. Go to the <b>Shift Templates</b> page to create some (e.g. Opening 10–18, Closing 18–24).
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="label-caps">Template</label>
              <select className="hos-input" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="apply-template-select">
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.start_time}–{displayEnd(t.end_time)}
                  </option>
                ))}
              </select>
              {template && (
                <div className="text-xs text-[color:var(--text-secondary)] mt-2">
                  {template.start_time} → {displayEnd(template.end_time)} · break {template.break_minutes}m · {template.role} / {template.department} · {(template.hours || 0).toFixed(1)}h
                </div>
              )}
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="label-caps">Days ({days.length} selected)</label>
                <div className="flex gap-2 text-xs">
                  <button className="text-[color:var(--gold)]" onClick={() => setDays([0,1,2,3,4,5,6])} data-testid="days-all">All</button>
                  <button className="text-[color:var(--text-secondary)]" onClick={() => setDays([0,1,2,3,4])} data-testid="days-weekdays">Weekdays</button>
                  <button className="text-[color:var(--text-secondary)]" onClick={() => setDays([])} data-testid="days-none">None</button>
                </div>
              </div>
              <div className="grid grid-cols-7 gap-2">
                {DAY_LABELS.map((lbl, i) => {
                  const active = days.includes(i);
                  return (
                    <button
                      key={lbl}
                      onClick={() => toggleDay(i)}
                      data-testid={`apply-day-${i}`}
                      className={`px-2 py-2 rounded-md border text-xs font-semibold ${active ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]" : "border-[color:var(--border)] text-[color:var(--text-secondary)] hover:border-[color:var(--gold)]"}`}
                    >
                      {lbl}
                      <div className="text-[10px] opacity-70 mt-0.5">{addDays(weekStart, i).slice(5)}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="label-caps">Employees ({employeeIds.length} selected)</label>
                <div className="flex gap-2 text-xs">
                  <button className="text-[color:var(--gold)]" onClick={() => setEmployeeIds(employees.map((e) => e.id))} data-testid="emps-all">All</button>
                  <button className="text-[color:var(--text-secondary)]" onClick={() => setEmployeeIds([])} data-testid="emps-none">None</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[240px] overflow-y-auto p-2 rounded-md border border-[color:var(--border)]">
                {employees.map((e) => {
                  const active = employeeIds.includes(e.id);
                  return (
                    <label key={e.id} className={`flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm ${active ? "bg-[color:var(--gold)]/10 border border-[color:var(--gold)]" : "border border-transparent hover:border-[color:var(--border)]"}`}>
                      <input type="checkbox" checked={active} onChange={() => toggleEmp(e.id)} data-testid={`apply-emp-${e.id}`}/>
                      <span className="truncate">{e.name}</span>
                      <span className="ml-auto text-[10px] text-[color:var(--text-secondary)] uppercase">{e.role}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="text-sm text-[color:var(--text-secondary)] mb-3">
              Will create <b className="text-[color:var(--gold)]" data-testid="apply-total">{totalShifts}</b> shift{totalShifts === 1 ? "" : "s"} ({employeeIds.length} employee × {days.length} day).
            </div>

            <div className="flex justify-end gap-3">
              <button className="hos-btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="hos-btn-primary" onClick={apply} disabled={busy || totalShifts === 0} data-testid="apply-template-confirm">
                Apply Template
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
